import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatSelectModule} from '@angular/material/select';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {Subject, takeUntil} from 'rxjs';
import {AuthenticationService} from '../../services/authentication/authentication.service';
import {RegistrationModel} from '../../models/registration/registration.model';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter} from '@angular/material/core';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {CurrencyPipe, NgClass, NgFor, NgIf} from '@angular/common';
import {PaymentService} from "../../services/payment/payment.service";
import {Payment, ShippingOption} from "../../models/payment/paymentOrder.model";
import {MatTableModule} from "@angular/material/table";
import {PartialSumPipe} from "../../pipe/partial-sum.pipe";
import {VatPipe} from "../../pipe/vat.pipe";
import {TotalSumPipe} from "../../pipe/total-sum.pipe";
import {MatCard} from "@angular/material/card";
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {InfoMessageService} from "../../services/infomessage/infomessage.service";
import { passwordValidator } from '../../validators/password-validator';
import { DeviceDetectionService } from '../../services/device-detection/device-detection.service';
import {MatMomentDateModule, MomentDateAdapter} from "@angular/material-moment-adapter";
import {ResendRegisterLinkModalComponent} from "../resend-register-link-modal/resend-register-link-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {MatCheckbox} from "@angular/material/checkbox";

export const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'DD.MM.YYYY',
    },
    display: {
        dateInput: 'DD.MM.YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

type Address = {
    city: string;
    dateOfBirth: string; // or Date, depending on how it's used
    email: string;
    firstName: string;
    houseNumber: string;
    lastName: string;
    phoneNumber: string;
    postalCode: string;
    salutation: string;
    streetName: string;
};

type Payload = {
    deliveryAddress: Address | null;
    invoiceAddress: Address;
};

@Component({
    selector: 'app-order-form',
	imports: [
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatSelectModule,
		MatTableModule,
		MatDatepicker,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSlideToggle,
		MatMomentDateModule,
		NgIf,
		NgFor,
		MatCard,
		PartialSumPipe,
		VatPipe,
		TotalSumPipe,
		CurrencyPipe,
		NgClass,
		MatCheckbox
	],
    providers: [
        provideNativeDateAdapter(MY_DATE_FORMATS),
        {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
        {provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS}
    ],
    templateUrl: './order-form.component.html',
    styleUrl: './order-form.component.sass'
})

export class OrderFormComponent implements OnInit, OnDestroy {
    private _formBuilder = inject(FormBuilder);
    private destroy$ = new Subject<void>();
    private readonly route = inject(ActivatedRoute);
    private requestInProgress: boolean = false;

    orderHash!: string;
    orderForm!: FormGroup;
    payment!: Payment;
    prepayment_qr_code!: SafeResourceUrl;
    shippingMethods!: ShippingOption[];
    selectedShippingMethod!: ShippingOption;

    streetFieldSeperated: boolean = false;
    isPaid: boolean = false;
    isMobile: boolean = false;
    selectedPayment: string = '';
    shippingMethodShippingIndex: number = 0;

    paymentMethods: any[] = [
        {
            name: "Vor-Ort-Bezahlung",
            value: "pickup",
        },
        {
            name: "Vorkasse",
            value: "prepayment"
        },
        {
            name:"PayPal",
            value: "paypal"
        }
    ];

    deliveryAddressActive: boolean = false;
    hidePassword: boolean = true;
	dataProtectionAgreementAccepted: boolean = false;
	termsAndConditionsAccepted: boolean = false;

    constructor(
        private authenticationService: AuthenticationService,
        private paymentService: PaymentService,
        private deviceDetectionService: DeviceDetectionService,
        private infoMessageService: InfoMessageService,
        private _sanitizer: DomSanitizer,
        private dialog: MatDialog,
		private router: Router
    ) {
        this.initForm();
        this.isMobile = deviceDetectionService.getIsMobile();
    }

    ngOnInit(): void {
        this.orderHash = this.route.snapshot.paramMap.get('orderHash') || '';

        if (this.orderHash) {
            this.getOrderByHash();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onShippingMethodChange(event: any): void {
		const pickup = this.paymentMethods.filter(method => method.name === 'Vor-Ort-Bezahlung');
		if (event.value.name.includes("Lieferung")) {
			pickup[0].disabled = true;
		} else {
			pickup[0].disabled = false;
		}
        this.selectedShippingMethod = event.value;
    }

    onPaymentMethodChange(event: any): void {
        if (event.value === 'pickup') {
            const pickup = this.shippingMethods.filter(method => method.name === 'Abholung');

            this.orderForm.get('general')?.patchValue({
                shippingMethod: pickup[0]
            })
            this.onShippingMethodChange({value:pickup[0]})
        }

        this.selectedPayment = event.value;
    }

    onSubmit() {
		if (!this.dataProtectionAgreementAccepted || !this.termsAndConditionsAccepted) {
			this.infoMessageService.createMessage('Bitte die Datenschutzbestimmungen und die AGBs akzeptieren', 'info');
			return;
		}
        if (this.requestInProgress) {
            return;
        }
        if (this.orderForm.invalid) {
            this.orderForm.markAllAsTouched();
            return;
        }

        let value = this.orderForm.getRawValue();

        const registrationModel: RegistrationModel = {
            deliveryAddress: value.deliveryAddress,
            invoiceAddress: value.address,
            email: value.general.email,
            firstName: value.address.firstName,
            salutation: value.address.salutation,
            lastName: value.address.lastName,
            mobileNumber: '',
            password: value.general.password,
            phoneNumber: value.address.phoneNumber,
            skype: '',
            title: '',
            dateOfBirth: value.address.dateOfBirth
        }
        this.requestInProgress = true;

        if (registrationModel.password) {
            this.registration(registrationModel)
        }

        if (this.payment.order.needsConfirmation) {
            this.confirmOrder();
        } else {
            this.startPayment();
        }
    }

    openResendLinkModal(): void {
        const dialogRef = this.dialog.open(ResendRegisterLinkModalComponent, {
            disableClose: true,
            closeOnNavigation: false
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.infoMessageService.createMessage('Falls Sie Ihre E-Mail-Adresse noch nicht bestätigt haben, erhalten Sie eine E-Mail mit einem neuem Bestätigungslink.')
            }
        })
    }

    private initForm(): void {
        this.orderForm = this._formBuilder.group({
            general: this._formBuilder.group(
                {
                    email: [{value: '', disabled: true}, [Validators.required, Validators.email]],
                    password: ['', passwordValidator()],
                    method: ['', Validators.required],
                    shippingMethod: ['', Validators.required],
                }
            ),
            address: this._formBuilder.group(
                {
                    firstName: ['', Validators.required],
                    lastName: ['', Validators.required],
                    salutation: [''],
                    dateOfBirth: [''],
                    phoneNumber: [''],
                    city: ['', Validators.required],
                    street: ['', Validators.required],
                    houseNumber: [''],
                    zipCode: ['', Validators.required],
                }
            ),
            deliveryAddress: this._formBuilder.group(
                {
                    firstName: [''],
                    lastName: [''],
                    salutation: [''],
                    dateOfBirth: [''],
                    phoneNumber: [''],
                    city: [''],
                    street: [''],
                    houseNumber: [''],
                    zipCode: [''],
                }
            ),
        })
    }

    patchOrderValues(payment: Payment): void {
        this.orderForm.get('general')?.patchValue({
            email: payment.order.invoiceAddress.email,
            method: payment.method !== 'none' ? payment.method : '',
            shippingMethod: this.selectedShippingMethod ? this.selectedShippingMethod : '',
        })
        this.orderForm.get('address')?.patchValue({
            firstName: payment.order.invoiceAddress.firstName,
            lastName: payment.order.invoiceAddress.lastName,
            dateOfBirth: payment.order.invoiceAddress.dateOfBirth,
            phoneNumber: payment.order.invoiceAddress.phoneNumber,
            salutation: payment.order.invoiceAddress.salutation,
            city: payment.order.invoiceAddress.city,
            street: payment.order.invoiceAddress.streetName,
            houseNumber: payment.order.invoiceAddress.houseNumber,
            zipCode: payment.order.invoiceAddress.postalCode,
        })

        this.streetFieldSeperated = payment.order.invoiceAddress.houseNumber !== '';

        if (payment.order.deliveryAddress) {
            this.deliveryAddressActive = true;

            this.orderForm.get('deliveryAddress')?.patchValue({
                firstName: payment.order.deliveryAddress.firstName,
                lastName: payment.order.deliveryAddress.lastName,
                dateOfBirth: payment.order.deliveryAddress.dateOfBirth,
                phoneNumber: payment.order.deliveryAddress.phoneNumber,
                salutation: payment.order.deliveryAddress.salutation,
                city: payment.order.deliveryAddress.city,
                street: payment.order.deliveryAddress.streetName,
                houseNumber: payment.order.invoiceAddress.houseNumber,
                zipCode: payment.order.deliveryAddress.postalCode,
            })
        }

        if (!this.payment.order.needsConfirmation) {
            this.orderForm.get('general')?.get('email')?.disable({onlySelf: true, emitEvent: false});

            this.orderForm.get('deliveryAddress')?.get('firstName')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('deliveryAddress')?.get('lastName')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('deliveryAddress')?.get('dateOfBirth')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('deliveryAddress')?.get('city')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('deliveryAddress')?.get('street')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('deliveryAddress')?.get('houseNumber')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('deliveryAddress')?.get('zipCode')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('deliveryAddress')?.get('phoneNumber')?.disable({onlySelf: true, emitEvent: false});

            this.orderForm.get('address')?.get('firstName')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('address')?.get('lastName')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('address')?.get('dateOfBirth')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('address')?.get('city')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('address')?.get('street')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('address')?.get('houseNumber')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('address')?.get('zipCode')?.disable({onlySelf: true, emitEvent: false});
            this.orderForm.get('address')?.get('phoneNumber')?.disable({onlySelf: true, emitEvent: false});
        }

    }

    toggleDeliveryAddress(value: boolean): void {
        this.deliveryAddressActive = value;

        if (this.deliveryAddressActive) {
            this.orderForm.get('deliveryAddress')?.get('firstName')?.setValidators([Validators.required]);
            this.orderForm.get('deliveryAddress')?.get('lastName')?.setValidators([Validators.required]);
            this.orderForm.get('deliveryAddress')?.get('dateOfBirth')?.setValidators([Validators.required]);
            this.orderForm.get('deliveryAddress')?.get('city')?.setValidators([Validators.required]);
            this.orderForm.get('deliveryAddress')?.get('street')?.setValidators([Validators.required]);

            if (this.streetFieldSeperated) {
                this.orderForm.get('deliveryAddress')?.get('houseNumber')?.setValidators([Validators.required]);
            }

            this.orderForm.get('deliveryAddress')?.get('zipCode')?.setValidators([Validators.required]);
        } else {
            this.orderForm.get('deliveryAddress')?.get('firstName')?.setValidators([]);
            this.orderForm.get('deliveryAddress')?.get('lastName')?.setValidators([]);
            this.orderForm.get('deliveryAddress')?.get('dateOfBirth')?.setValidators([]);
            this.orderForm.get('deliveryAddress')?.get('city')?.setValidators([]);
            this.orderForm.get('deliveryAddress')?.get('street')?.setValidators([]);
            this.orderForm.get('deliveryAddress')?.get('houseNumber')?.setValidators([]);
            this.orderForm.get('deliveryAddress')?.get('zipCode')?.setValidators([]);
        }
    }

    private getOrderByHash(): void {
        this.paymentService.getPaymentOrderByHash(this.orderHash)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    if (response.shippingMethods.length > 0) {
                        this.shippingMethods = response.shippingMethods
                        this.shippingMethodShippingIndex = this.shippingMethods.findIndex(method => method.name === 'Lieferung')
                    } else {
                        this.shippingMethods = response.shippingMethods
                        this.onShippingMethodChange(
                            {value:response.payment.order.shipping}
                        );
                    }

                    if (response.payment.status.includes("paid/")) {
                        this.isPaid = true;
                        return;
                    }

                    this.payment = response.payment;

                    if (this.payment.payment.qr_code) {
                        this.prepayment_qr_code = this._sanitizer.bypassSecurityTrustResourceUrl('data:image/jpg;base64,'
                            + this.payment.payment.qr_code);
                    }
                    this.patchOrderValues(response.payment);
                },
                error: (error) => {
                    console.error('error', error);
                }
            })
    }

    private registration(registrationModel: RegistrationModel): void {
        this.authenticationService.registration(registrationModel)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.infoMessageService.createMessage('Ihre Registrierung war erfolgreich!' , 'success')
                },
                error: (error) => {
                    this.infoMessageService.createMessage('Es gab einen Fehler bei der Registrierung!' ,'error')
                }
            })
    }

    private confirmOrder(): void {
        const values = this.orderForm.getRawValue();

        let payload: Payload = {
            deliveryAddress: {
                city: values.deliveryAddress.city,
                dateOfBirth: values.deliveryAddress.dateOfBirth,
                email: values.general.email,
                firstName: values.deliveryAddress.firstName,
                houseNumber: values.deliveryAddress.houseNumber,
                lastName: values.deliveryAddress.lastName,
                phoneNumber: values.deliveryAddress.phoneNumber,
                postalCode: values.deliveryAddress.zipCode,
                salutation: values.deliveryAddress.salutation,
                streetName: values.deliveryAddress.street
            },
            invoiceAddress: {
                city: values.address.city,
                dateOfBirth: values.address.dateOfBirth,
                email: values.general.email,
                firstName: values.address.firstName,
                houseNumber: values.address.houseNumber,
                lastName: values.address.lastName,
                phoneNumber: values.address.phoneNumber,
                postalCode: values.address.zipCode,
                salutation: values.address.salutation,
                streetName: values.address.street
            }
        }

        if (!this.deliveryAddressActive) {
            payload.deliveryAddress = null;
        }

        this.paymentService.confirmOrder(payload, this.orderHash)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.startPayment();
                },
                error: (error) => {
                    this.requestInProgress = false;
                    this.infoMessageService.createMessage('Die Bestellung konnte nicht bestätigt werden!' ,'error')
                }
            })
    }

    startPayment(): void {
        this.paymentService.startPaymentProcess(this.orderHash, this.selectedShippingMethod, this.orderForm.value.general.method)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.payment = response;
                    if (this.payment.payment.qr_code) {
                        this.prepayment_qr_code = this._sanitizer.bypassSecurityTrustResourceUrl('data:image/jpg;base64,'
                            + this.payment.payment.qr_code);
                    }
                    this.patchOrderValues(response);
                    this.requestInProgress = false;
                },
                error: (error) => {
                    this.requestInProgress = false;
                    this.infoMessageService.createMessage('Die Bezahlung konnte nicht gestartet werden!' ,'error')
                }
            })
    }

	toDataProtectionSite(): void {
		const fullUrl = this.router.serializeUrl(
			this.router.createUrlTree(['/datenschutz'])
		);

		// Open the URL in a new window or tab
		window.open(fullUrl, '_blank');
	}

	toAgbsPage(): void {
		const fullUrl = this.router.serializeUrl(
			this.router.createUrlTree(['/agb'])
		);

		// Open the URL in a new window or tab
		window.open(fullUrl, '_blank');
	}
}
